import { useEffect, useMemo, useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  Dialog,
  Flex,
  Form,
  Input,
  Notice,
  Select,
  Text,
  VisuallyHidden,
  toast,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useTimezone } from '~publish/hooks/useTimezone'

import type { PostCard_PostData } from '../fragments/PostCard_Post'
import type { RecurrenceFrequency } from './recurrenceHelpers'
import { buildSummary } from './recurrenceHelpers'
import {
  useCancelPostRecurrence,
  useCreatePostRecurrence,
  useEditPostRecurrence,
} from './usePostRecurrence'

import styles from './RecurrenceSettingsDialog.module.css'

const FREQUENCIES = new Set<string>(['day', 'week', 'month', 'year'])

const MAX_AMOUNT: Record<RecurrenceFrequency, number> = {
  day: 100,
  week: 30,
  month: 6,
  year: 5,
}

function isRecurrenceFrequency(value: string): value is RecurrenceFrequency {
  return FREQUENCIES.has(value)
}

function clampNumeric(value: number, min = 1, max = 100): number {
  return Math.max(min, Math.min(max, Math.round(value) || min))
}

type RecurrenceSettingsDialogContentProps = {
  onOpenChange: (open: boolean) => void
  mode: 'create' | 'edit'
  postId: string
  channelId: string
  channelType: string
  channelService: string
  dueAt?: string | null
  recurrence?: PostCard_PostData['recurrence']
}

export function RecurrenceSettingsDialogContent({
  onOpenChange,
  mode,
  postId,
  channelId,
  channelType,
  channelService,
  dueAt,
  recurrence,
}: RecurrenceSettingsDialogContentProps): JSX.Element {
  const [frequency, setFrequency] = useState<RecurrenceFrequency>(
    recurrence?.cadence.frequency ?? 'week',
  )
  const [cadenceIntervalInput, setCadenceIntervalInput] = useState(
    String(recurrence?.cadence.frequencyStep ?? 1),
  )
  const [amountInput, setAmountInput] = useState(
    String(
      mode === 'edit'
        ? (recurrence?.recurrentPostsLeft ?? recurrence?.maxRecurrentPosts ?? 1)
        : (recurrence?.maxRecurrentPosts ?? 1),
    ),
  )
  const timeZone = useTimezone()

  useEffect(() => {
    BufferTracker.recurrenceDialogViewed({
      clientName: 'publishWeb',
      postId,
      channelId,
      channelType,
      channel: channelService,
      isExistingRecurrence: mode === 'edit',
    })
  }, [postId, channelId, channelType, channelService, mode])

  const [createRecurrence, { loading: createLoading }] =
    useCreatePostRecurrence()
  const [editRecurrence, { loading: editLoading }] = useEditPostRecurrence()
  const [cancelRecurrence, { loading: cancelLoading }] =
    useCancelPostRecurrence()

  const isSaving = createLoading || editLoading
  const liveInterval = Number(cadenceIntervalInput) || 0
  const liveAmount = Number(amountInput) || 0
  const amountExceedsLimit = liveAmount > MAX_AMOUNT[frequency]

  const summary = useMemo(() => {
    if (!dueAt || !liveInterval || !liveAmount) return null
    const startDate = new Date(dueAt)
    if (isNaN(startDate.getTime())) return null
    return buildSummary(
      startDate,
      frequency,
      liveInterval,
      liveAmount,
      timeZone,
    )
  }, [dueAt, frequency, liveInterval, liveAmount, timeZone])

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()

    const finalInterval = clampNumeric(
      Number(cadenceIntervalInput),
      1,
      MAX_AMOUNT[frequency] * 4,
    )
    const finalAmount = Math.max(1, Math.round(Number(amountInput)) || 1)
    const cadence = {
      frequency,
      frequencyStep: finalInterval,
    }

    const { success, error } =
      mode === 'create'
        ? await createRecurrence({
            variables: { id: postId, cadence, maxRecurrentPosts: finalAmount },
          })
        : await editRecurrence({
            variables: { id: postId, cadence, recurrentPostsLeft: finalAmount },
          })

    if (success) {
      toast.success(
        mode === 'create' ? 'Recurrence created' : 'Recurrence updated',
      )
      onOpenChange(false)
    } else {
      toast.error(error.message || 'Something went wrong')
    }
  }

  const handleCancel = async (): Promise<void> => {
    const { success, error } = await cancelRecurrence({
      variables: { id: postId },
    })

    if (success) {
      toast.success('Recurrence removed')
      onOpenChange(false)
    } else {
      toast.error(error.message || 'Failed to remove recurrence')
    }
  }

  const handleClose = (): void => {
    onOpenChange(false)
  }

  return (
    <Dialog.Content size="medium">
      <Dialog.Header>
        <Dialog.Title>
          {mode === 'create' ? 'Recurring Post' : 'Edit Recurrence'}
        </Dialog.Title>
        <Dialog.Description>
          We&apos;ll schedule future occurrences based on this post&apos;s date
          and time.
        </Dialog.Description>
        <Dialog.CloseButton />
      </Dialog.Header>

      <Dialog.Body className={styles.content}>
        <Flex direction="column" gap="lg">
          <Form
            id="recurrence-settings-form"
            onSubmit={(e): void => {
              void handleSubmit(e)
            }}
          >
            <Flex align="center" gap="sm" wrap="wrap">
              <Text>Repeat every</Text>

              <Flex align="center" gap="xs">
                <VisuallyHidden as="label" htmlFor="interval-input">
                  Recurrence interval
                </VisuallyHidden>
                <Input
                  id="interval-input"
                  value={cadenceIntervalInput}
                  onChange={(event): void =>
                    setCadenceIntervalInput(event.target.value)
                  }
                  onBlur={(): void => {
                    const clamped = clampNumeric(
                      Number(cadenceIntervalInput),
                      1,
                      MAX_AMOUNT[frequency] * 4,
                    )
                    setCadenceIntervalInput(String(clamped))
                  }}
                  min={1}
                  max={MAX_AMOUNT[frequency] * 4}
                  step="1"
                  type="number"
                  className={styles.counterInput}
                />

                <VisuallyHidden as="label" htmlFor="interval-type">
                  Recurrence interval type
                </VisuallyHidden>
                <Select
                  value={frequency}
                  onValueChange={(value): void => {
                    if (isRecurrenceFrequency(value)) {
                      setFrequency(value)
                    }
                  }}
                >
                  <Select.Trigger size="medium" />
                  <Select.Content>
                    <Select.Item value="day">
                      {liveInterval === 1 ? 'Day' : 'Days'}
                    </Select.Item>
                    <Select.Item value="week">
                      {liveInterval === 1 ? 'Week' : 'Weeks'}
                    </Select.Item>
                    <Select.Item value="month">
                      {liveInterval === 1 ? 'Month' : 'Months'}
                    </Select.Item>
                    <Select.Item value="year">
                      {liveInterval === 1 ? 'Year' : 'Years'}
                    </Select.Item>
                  </Select.Content>
                </Select>
              </Flex>

              <Text>for</Text>

              <VisuallyHidden as="label" htmlFor="interval-length">
                Recurrence duration
              </VisuallyHidden>
              <Input
                id="interval-length"
                value={amountInput}
                onChange={(event): void => setAmountInput(event.target.value)}
                onBlur={(): void => {
                  const clamped = Math.max(
                    1,
                    Math.round(Number(amountInput)) || 1,
                  )
                  setAmountInput(String(clamped))
                }}
                min={1}
                step="1"
                type="number"
                className={styles.counterInput}
              />

              <Text>time{liveAmount !== 1 ? 's' : ''}</Text>
            </Flex>
          </Form>

          {amountExceedsLimit && (
            <Notice variant="warning">
              The maximum number of times for{' '}
              {frequency === 'day'
                ? 'daily'
                : frequency === 'week'
                  ? 'weekly'
                  : frequency === 'month'
                    ? 'monthly'
                    : 'yearly'}{' '}
              recurrence is {MAX_AMOUNT[frequency]}. Please lower the value to
              continue.
            </Notice>
          )}

          {summary && !amountExceedsLimit && <Notice>{summary}</Notice>}
        </Flex>
      </Dialog.Body>

      <Dialog.Footer>
        {mode === 'edit' && (
          <Button
            variant="critical-subtle"
            loading={cancelLoading}
            onClick={handleCancel}
            className={styles.removeButton}
          >
            Stop Recurrence
          </Button>
        )}
        <Button variant="tertiary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          form="recurrence-settings-form"
          loading={isSaving}
          disabled={amountExceedsLimit}
        >
          Save
        </Button>
      </Dialog.Footer>
    </Dialog.Content>
  )
}
